import { Flex } from "../../components/Flex";
import { H3 } from "../../components/Heading";
import { Loader } from "../../components/Loader";
import { Slider } from "../../components/Slider";
import { useIndividualCheck } from "../../data/useIndividualCheck";
import { Box } from "../../components/Box";
import { TableHead } from "../../components/Table/TableHead";
import { TableBody } from "../../components/Table/TableBody";
import { TableRow } from "../../components/Table/TableRow";
import { TableCell } from "../../components/Table/TableCell";
import { Image } from "../../components/Image";
import {
  CheckContainer,
  CheckHeading,
  CheckParagraph,
  CheckTable,
  CheckTableCell,
  CheckTableHeaderRow,
  CheckTitle,
  Section,
} from "./CheckStyledComponents";
import {
  IndividualCheckStatus,
  CheckStatusEnum,
} from "../../context/MandateStatusContext";
import success from "../../assets/card-success-icon.svg";
import inReview from "../../assets/card-in-review-icon.svg";
import { ManualAcceptReject } from "./ManualAcceptReject";
import { IndividualChecksMap } from "./helper";
import { CheckResults } from "./CheckResults";
import { CheckFields } from "./CheckFields";
import { ApprovalComment } from "./approvalComment";
import { AddressIndividualInformation } from "./AddressIndividualInformation";
import { useIndividual } from "../../data/useIndividual";
import { useParams } from "react-router-dom";
import { formatDate } from "../../utils/formatters";
import { Paragraph } from "../../components/Paragraph";
import { DateTime } from "luxon";

const hasObjectData = (value) =>
  !!value &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  Object.values(value).some(
    (item) => item !== null && item !== undefined && item !== "",
  );

const hasArrayData = (value) => Array.isArray(value) && value.length > 0;

const hasAddressData = (address) =>
  hasObjectData(address) &&
  (!!address?.address ||
    !!address?.address_type ||
    hasArrayData(address?.address_sources));

const getCreatedTimestamp = (source) => {
  const date = source?.first_date_created;
  if (!date) return 0;

  const isoDate = DateTime.fromISO(date);
  if (isoDate.isValid) return isoDate.toMillis();

  const slashDate = DateTime.fromFormat(date, "dd/MM/yyyy");
  if (slashDate.isValid) return slashDate.toMillis();

  const dashDate = DateTime.fromFormat(date, "dd-MM-yyyy");
  if (dashDate.isValid) return dashDate.toMillis();

  const timestamp = Date.parse(date);

  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const getLatestCreatedTimestamp = (address) =>
  hasArrayData(address?.address_sources)
    ? Math.max(...address.address_sources.map(getCreatedTimestamp))
    : 0;

const matchDataFields = [
  ["id_number", "National ID"],
  ["passport_number", "Passport Number"],
  ["first_name", "First Name"],
  ["second_name", "Second Name"],
  ["third_name", "Third Name"],
  ["surname", "Surname"],
  ["initials", "Initials"],
  ["birth_date", "Date Of Birth"],
  ["gender", "Gender"],
  ["deceased", "Deceased"],
  ["verified", "Verified"],
  ["date_verified", "Date Verified"],
  ["identity_type", "Identity Type"],
];

const DataSourceMatchingDetails = ({ data }) => {
  if (!hasObjectData(data)) {
    return null;
  }

  return (
    <Box mb={4}>
      <H3 mb={0}>Data Source Matching Details</H3>
      <Paragraph color={"gray.60"} mt={0} mb={2} fontSize={1}>
        The following personal information was collected from the data source
      </Paragraph>
      {matchDataFields.map(([key, label]) =>
        data?.[key] ? (
          <Flex key={key} alignItems="center">
            <CheckHeading minWidth={180}>{label}:</CheckHeading>
            <CheckParagraph>{data[key]}</CheckParagraph>
          </Flex>
        ) : null,
      )}
    </Box>
  );
};

const DataSourceAddressDetails = ({ addresses }) => {
  const addressesWithData = hasArrayData(addresses)
    ? addresses
        .filter(hasAddressData)
        .sort(
          (a, b) => getLatestCreatedTimestamp(b) - getLatestCreatedTimestamp(a),
        )
    : [];

  if (!hasArrayData(addressesWithData)) {
    return null;
  }

  return (
    <Box mb={4}>
      <H3>Data Source Address Details</H3>
      <Paragraph color={"gray.60"} mt={0} mb={2} fontSize={1}>
        The following address details were collected from the data source
      </Paragraph>
      {addressesWithData.map((address, index) => {
        const addressSources = hasArrayData(address?.address_sources)
          ? [...address.address_sources].sort(
              (a, b) => getCreatedTimestamp(b) - getCreatedTimestamp(a),
            )
          : [];

        return (
          <Box key={`${address?.address || "address"}-${index}`} mb={3}>
            <Box mb={2}>
              <CheckHeading mb={3}>
                {address?.address_type
                  ? `${address.address_type} Address`
                  : "Address"}
              </CheckHeading>
            </Box>
            {address?.address && (
              <Flex mb={2}>
                <CheckHeading mr={1}>Address:</CheckHeading>
                <Paragraph my={0}>{address.address}</Paragraph>
              </Flex>
            )}

            {hasArrayData(addressSources) && (
              <CheckTable>
                <TableHead></TableHead>
                <TableBody>
                  <CheckTableHeaderRow>
                    <CheckTableCell width="40%">Subscriber name</CheckTableCell>
                    <CheckTableCell width="30%">Date Created</CheckTableCell>
                    <CheckTableCell width="30%">Last Updated</CheckTableCell>
                  </CheckTableHeaderRow>
                  {addressSources.map((source, sourceIndex) => (
                    <TableRow
                      key={`${
                        source?.subscriber_name || "source"
                      }-${sourceIndex}`}
                    >
                      <TableCell
                        width="40%"
                        color="gray.60"
                        borderBottom="none"
                      >
                        {source?.subscriber_name || "-"}
                      </TableCell>
                      <TableCell
                        width="30%"
                        color="gray.60"
                        borderBottom="none"
                      >
                        {formatDate(source?.first_date_created)}
                      </TableCell>
                      <TableCell
                        width="30%"
                        color="gray.60"
                        borderBottom="none"
                      >
                        {formatDate(source?.last_date_updated)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </CheckTable>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export const AddressVerificationCheck = (props) => {
  const { isOpen, onRequestClose, id, useSlider = true } = props;
  const params = useParams();

  const { data: individualCheckData, isLoading: isLoadingIndividualCheck } =
    useIndividualCheck(id);

  const { data: individualData } = useIndividual(params.id);

  const content = (
    <CheckContainer>
      <Flex justifyContent="space-between">
        <CheckTitle>
          {IndividualChecksMap.address_verification_check.title}
        </CheckTitle>

        {individualCheckData?.data?.status?.status ===
          CheckStatusEnum.Review && (
          <ManualAcceptReject
            title={IndividualChecksMap.address_verification_check.title}
            id={id}
            isMonitoring={false}
          ></ManualAcceptReject>
        )}
      </Flex>
      {individualCheckData?.data?.status?.status && (
        <CheckResults
          status={individualCheckData?.data?.status?.status}
        ></CheckResults>
      )}
      {individualCheckData?.data?.instance &&
        individualCheckData?.data?.instance?.approval_comment && (
          <ApprovalComment
            data={individualCheckData?.data?.instance?.approval_comment}
            status={individualCheckData?.data?.status?.status}
          ></ApprovalComment>
        )}
      <CheckFields
        individual_checkfield_result={
          individualCheckData?.data?.individual_checkfield_result
        }
      ></CheckFields>

      {individualCheckData?.data?.instance?.results?.map((instance, index) => (
        <Section key={index}>
          <Flex
            py={2}
            mb={2}
            borderBottom={"1px solid"}
            borderColor="gray.10"
            alignItems="center"
          >
            <Box mr={2}>
              <Image
                src={
                  instance?.status === IndividualCheckStatus.Passed
                    ? success
                    : inReview
                }
                alt="status icon"
                height="20px"
                width="auto"
                mr={1}
              />
            </Box>

            <Box>
              <H3 mb={0}>{instance?.title}</H3>
              <CheckParagraph>{instance?.description}</CheckParagraph>
            </Box>
          </Flex>

          <Flex mb={2} alignItems="center">
            <CheckHeading mr={1}>Result:</CheckHeading>
            <CheckParagraph>{instance?.status}</CheckParagraph>
          </Flex>

          <Flex mb={2} alignItems="center">
            <CheckHeading mr={1}>Source:</CheckHeading>
            <CheckParagraph>{instance?.description}</CheckParagraph>
          </Flex>

          <Box mb={2}>
            <CheckHeading>Details:</CheckHeading>
            <CheckTable>
              <TableHead></TableHead>
              <TableBody>
                <CheckTableHeaderRow>
                  <CheckTableCell>Result</CheckTableCell>
                  <CheckTableCell>Key</CheckTableCell>
                  <CheckTableCell>Reason</CheckTableCell>
                </CheckTableHeaderRow>
                {instance?.reasons?.map(({ status, key, reason }, i) => (
                  <TableRow key={i}>
                    <TableCell
                      width="15%"
                      style={{ textTransform: "uppercase" }}
                      fontWeight="500"
                      color="gray.80"
                      borderBottom="none"
                    >
                      <Flex gap="8">
                        {/* <Image
                              src={
                                status === IndividualCheckStatus.Passed
                                  ? success
                                  : inReview
                              }
                              alt="status icon"
                              height="20px"
                              width="auto"
                              mr={1}
                            /> */}
                        {status}
                      </Flex>
                    </TableCell>
                    <TableCell color="gray.60" borderBottom="none">
                      {key}
                    </TableCell>
                    <TableCell color="gray.60" borderBottom="none">
                      {reason}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </CheckTable>
          </Box>

          {individualData?.data && individualData?.data?.personal_detail && (
            <AddressIndividualInformation
              data={individualData.data}
            ></AddressIndividualInformation>
          )}

          <DataSourceMatchingDetails
            data={instance?.meta_data?.definite_match_data}
          />

          <DataSourceAddressDetails
            addresses={instance?.meta_data?.addresses}
          />
        </Section>
      ))}
    </CheckContainer>
  );

  return (
    <>
      {isLoadingIndividualCheck ? (
        <Flex justifyContent="center" alignItems="center" minHeight="60vh">
          <Loader />
        </Flex>
      ) : useSlider ? (
        <Slider
          isOpen={isOpen}
          onRequestClose={() => onRequestClose(false)}
          width="70%"
        >
          {content}
        </Slider>
      ) : (
        content
      )}
    </>
  );
};
